/**
 * Genetec "Video source" (Camera Admin, site level). A Genetec site streams
 * through the Media Gateway by default; set to `direct` the connector dials
 * each camera unit itself (host, RTSP port, stream path, camera login), so
 * those per-camera connection fields must be editable even while empty.
 *
 * The site About payload (`customer/{id}/about/`) carries the setting as an
 * `extra_fields` entry with `id: "genetec_video_source"`. Site About reads
 * extra-field values off the top level of the payload (`siteInfo[field.id]`),
 * so both shapes are accepted here: a `value` on the entry, or the top-level
 * key.
 *
 * `camera/{id}/general_info/` adds two read-only fields per camera:
 * `video_source` (the effective source for this camera) and
 * `video_source_reason` (why it fell back, e.g. no host set).
 */
export type GenetecVideoSource = "media_gateway" | "direct";

export const GENETEC_VIDEO_SOURCE_FIELD = "genetec_video_source";

export const VIDEO_SOURCE_LABELS: Record<GenetecVideoSource, string> = {
  media_gateway: "Media Gateway",
  direct: "Direct",
};

export const VIDEO_SOURCE_COLUMN_TITLE = "Video source";

type SiteExtraField = { id: string | number; value?: unknown };

export type SiteLike =
  | {
      integration_type_name?: string | null;
      extra_fields?: SiteExtraField[] | null;
      [key: string]: unknown;
    }
  | null
  | undefined;

export const isGenetecSite = (siteInfo: SiteLike): boolean =>
  String(siteInfo?.integration_type_name ?? "").toLowerCase() === "genetec";

export const normalizeVideoSource = (
  raw: unknown
): GenetecVideoSource | null => {
  const value = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (value === "direct" || value === "media_gateway") {
    return value;
  }
  return null;
};

/**
 * The site's Video source. `null` for every non-Genetec site; a Genetec site
 * with no value yet (backend not shipped, field unset) reads as the admin
 * default, `media_gateway`, so today's behaviour is unchanged until the site
 * is switched to direct.
 */
export const getSiteVideoSource = (
  siteInfo: SiteLike
): GenetecVideoSource | null => {
  if (!isGenetecSite(siteInfo)) {
    return null;
  }
  const entry = (siteInfo?.extra_fields ?? []).find(
    (field) => String(field?.id) === GENETEC_VIDEO_SOURCE_FIELD
  );
  return (
    normalizeVideoSource(entry?.value) ??
    normalizeVideoSource(siteInfo?.[GENETEC_VIDEO_SOURCE_FIELD]) ??
    "media_gateway"
  );
};

export const isGenetecDirectSite = (siteInfo: SiteLike): boolean =>
  getSiteVideoSource(siteInfo) === "direct";

/** "Direct" / "Media Gateway" for the read-only column; "--" when unknown. */
export const formatVideoSource = (raw: unknown): string => {
  const source = normalizeVideoSource(raw);
  return source ? VIDEO_SOURCE_LABELS[source] : "--";
};

/**
 * What the Video source column shows for a camera: the camera's effective
 * source when the API sends one, else the site-level setting (covers the
 * window before the general_info fields ship).
 */
export const getCameraVideoSourceLabel = (
  cameraObject: { video_source?: unknown } | null | undefined,
  siteInfo: SiteLike
): string =>
  formatVideoSource(cameraObject?.video_source ?? getSiteVideoSource(siteInfo));

/** Placeholders for the empty inputs on a Genetec direct camera. */
export const GENETEC_DIRECT_PLACEHOLDERS = {
  host: "10.0.0.5",
  rtsp: "554",
  stream_parameters: "/media/video2",
};
